/** @fileoverview Screen-level store of the filter chips picked in each list, so charts on the same screen can follow them. */

import { createContext, useContext } from 'react';
import { createStore, useStore, type StoreApi } from 'zustand';

import type { FabrikListFilter, FabrikListFilterChoices } from '../../types/apps';

/** A picked chip value, keyed `${filter.id}:${index}` like the list has always keyed them. */
export type ListFilterValues = Record<string, string | number | boolean>;

type ListFilterState = {
  /** Picked chip values per list id. A list with nothing picked has no entry. */
  valuesByList: Record<string, ListFilterValues>;
  /** Sets one chip of one list, or clears it when `value` is undefined. */
  setFilterValue: (change: {
    listId: string;
    filterKey: string;
    value: string | number | boolean | undefined;
  }) => void;
  /** Clears every chip of one list. */
  clearList: (listId: string) => void;
};

export type ListFilterStore = StoreApi<ListFilterState>;

const EMPTY_VALUES: ListFilterValues = {};

/**
 * Key of one filter chip of a list.
 * @param filter the list filter
 * @param filterIndex its position in the list's filters
 */
export const filterKeyOf = (filter: FabrikListFilter, filterIndex: number) =>
  `${filter.id}:${filterIndex}`;

/**
 * Creates an empty store. One store lives with one rendered screen, so its chips reset when the
 * screen unmounts, the same as the list's own state did before.
 */
export const createListFilterStore = (): ListFilterStore =>
  createStore<ListFilterState>()((set) => ({
    valuesByList: {},
    setFilterValue: ({ listId, filterKey, value }) =>
      set(({ valuesByList }) => {
        const next = { ...(valuesByList[listId] ?? EMPTY_VALUES) };
        if (value === undefined) {
          delete next[filterKey];
        } else {
          next[filterKey] = value;
        }
        return { valuesByList: { ...valuesByList, [listId]: next } };
      }),
    clearList: (listId) =>
      set(({ valuesByList }) => ({ valuesByList: { ...valuesByList, [listId]: EMPTY_VALUES } })),
  }));

/* Only a list drawn outside a screen reads this one; every screen gives its own store. */
const fallbackStore = createListFilterStore();

const ListFilterStoreContext = createContext<ListFilterStore>(fallbackStore);

export const ListFilterStoreProvider = ListFilterStoreContext.Provider;

/** Returns the screen's list filter store. */
export const useListFilterStore = () => useContext(ListFilterStoreContext);

/**
 * Returns the picked chips of one list and the functions that change them.
 * @param listId id of the list
 */
export const useListFilterValues = (listId: string) => {
  const store = useListFilterStore();
  const values = useStore(store, ({ valuesByList }) => valuesByList[listId] ?? EMPTY_VALUES);
  const setFilterValue = useStore(store, (state) => state.setFilterValue);
  const clearList = useStore(store, (state) => state.clearList);
  return { values, setFilterValue, clearList };
};

/**
 * Turns the picked chips of the given lists into the shape the charts read sends:
 * `{ [listId]: [{ index, value }] }`, in filter order. Lists with nothing picked are left out.
 * @param valuesByList picked chip values per list id
 * @param filtersByList each list's configured filters
 */
export const toListFilterChoices = (
  valuesByList: Record<string, ListFilterValues>,
  filtersByList: Record<string, FabrikListFilter[]>
): FabrikListFilterChoices =>
  Object.fromEntries(
    Object.entries(filtersByList).flatMap(([listId, filters]) => {
      const values = valuesByList[listId] ?? EMPTY_VALUES;
      const choices = filters.flatMap((filter, filterIndex) => {
        const value = values[filterKeyOf(filter, filterIndex)];
        return value === undefined ? [] : [{ index: filterIndex, value }];
      });
      return choices.length ? [[listId, choices]] : [];
    })
  );
